/* React */
import React, { useMemo, useRef, useEffect } from "react";

/* React Redux */
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";

/* d3.js */
import * as d3 from "d3";

/* Utils */
import { getUnitFromFeatureOrCohort } from "src/utils/helper/charts/helper";
import { useEffectiveShowNoValueEntries } from "src/utils/hooks/useEffectiveShowNoValueEntries";
import {
  excludeCohortNoValueGroups,
  orderCohortGroups,
} from "src/shared/constants/charts/NoValueStates";

/* Components */
import ChartTooltip from "src/components/shared/chart-tooltip/ChartTooltip";
import { createStackedHistogram } from "src/components/charts/stacked-histogram/stackedHistogramRenderer";

/* Interfaces */
import { HistogramProps } from "src/shared/interfaces/charts/Histogram";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { ChartTooltipRef } from "src/shared/interfaces/ChartTooltip";

const StackedHistogram: React.FC<{
  data: HistogramProps[];
  dimensions: ChartDimensionsProps;
}> = ({ data, dimensions }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<ChartTooltipRef>(null);
  const height: number = 492;

  const featureTypesState = useSelector(
    (state: RootState) => state.featureTypes
  );

  const { data: featureData } = featureTypesState;

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { features } = insightsFilterState;

  // Gated by custom-cohort state so the histogram never renders grey no-value
  // bins the request (and the page/legend) treat as off - see the hook's docs.
  const showNoValueEntries = useEffectiveShowNoValueEntries();

  const unit = getUnitFromFeatureOrCohort(
    featureData,
    features
  );

  const chartData = useMemo(
    () =>
      orderCohortGroups(
        excludeCohortNoValueGroups(data, showNoValueEntries)
      ),
    [data, showNoValueEntries]
  );

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    createStackedHistogram(
      svg,
      tooltipRef,
      chartData,
      dimensions,
      unit,
      showNoValueEntries
    );
  }, [chartData, dimensions, unit, showNoValueEntries]);

  return (
    <>
      <ChartTooltip ref={tooltipRef} />
      <svg
        ref={svgRef}
        height={height}
        width={dimensions.width}
      />
    </>
  );
};

export default StackedHistogram;
